/* @jsx h */
import { defineComponent, h } from "vue";
import type { ReservationSettingsUpdateHandler } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

export interface BookingRulesProps {
  mode: "table_based" | "fixed_amount";
  maxGuests: number;
  defaultPeriodMinutes: number;
  onUpdate: ReservationSettingsUpdateHandler;
}

export const BookingRules = defineComponent<BookingRulesProps>({
  name: "BookingRules",
  props: ["mode", "maxGuests", "defaultPeriodMinutes", "onUpdate"],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="space-y-6">
        <div class="grid gap-6 md:grid-cols-2">
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">
                {t("settings.reservations.availability_mode")}
              </span>
            </label>
            <select
              class="select select-bordered w-full"
              value={props.mode}
              onChange={(e) => {
                const val = (e.target as HTMLSelectElement).value as
                  | "table_based"
                  | "fixed_amount";
                props.onUpdate("reservation_algorithm", val);
              }}
            >
              <option value="table_based">
                {t("settings.reservations.table_based")}
              </option>
              <option value="fixed_amount">
                {t("settings.reservations.fixed_amount")}
              </option>
            </select>
            <label class="label">
              <span class="label-text-alt ">
                {t("settings.reservations.availability_mode_desc")}
              </span>
            </label>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">
                {t("settings.reservations.max_party_size")}
              </span>
            </label>
            <input
              type="number"
              class="input input-bordered w-full"
              value={props.maxGuests}
              onInput={(e) =>
                props.onUpdate(
                  "reservations_max_party_size",
                  parseInt((e.target as HTMLInputElement).value),
                )
              }
            />
            <label class="label">
              <span class="label-text-alt ">
                {t("settings.reservations.max_party_size_desc")}
              </span>
            </label>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">
                {t("settings.reservations.default_period")}
              </span>
            </label>
            <label class="input input-bordered w-full flex items-center gap-2">
              <input
                type="number"
                min="15"
                step="5"
                class="grow"
                value={props.defaultPeriodMinutes}
                onInput={(e) => {
                  const parsed = parseInt(
                    (e.target as HTMLInputElement).value,
                    10,
                  );
                  props.onUpdate(
                    "reservations_time_slot_minutes",
                    Number.isFinite(parsed)
                      ? parsed
                      : props.defaultPeriodMinutes,
                  );
                }}
              />
              <span class="text-sm ">
                {t("settings.reservations.minutes_suffix")}
              </span>
            </label>
            <label class="label">
              <span class="label-text-alt ">
                {t("settings.reservations.default_period_desc")}
              </span>
            </label>
          </div>
        </div>
      </div>
    );
  },
});
